import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { departmentService } from '../../services/departmentService';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { 
  SECTORS,
  USER_ROLES,
  isNonDepartmentRole,
  USER_STATUS,
  ROLE_LABELS,
  USER_STATUS_OPTIONS
} from '../../constants/authConstants';

const AddUserModal = ({ isOpen, onClose, onUserAdded }) => {
  const { isMobile, getResponsiveValue } = useTheme();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    sector: '',
    department: '',
    role: USER_ROLES.DEPARTMENT,
    status: USER_STATUS.ACTIVE
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const gridCols = getResponsiveValue({
    mobile: 'grid-cols-1',
    desktop: 'grid-cols-2'
  });

  const gridGap = getResponsiveValue({
    mobile: 'gap-3',
    desktop: 'gap-4'
  });

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const buttonSpacing = getResponsiveValue({
    mobile: 'flex-col space-y-3',
    desktop: 'flex-row space-x-3'
  });

  useEffect(() => {
    if (isOpen) {
      loadDepartments();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.sector) {
      const depts = departments.filter(d => 
        d.sector === formData.sector && d.isActive
      );
      setFilteredDepartments(depts);
    } else {
      setFilteredDepartments([]);
    }
  }, [formData.sector, departments]);

  const loadDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const allDepartments = await departmentService.getActiveDepartments();
      setDepartments(allDepartments);
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    if (field === 'sector') {
      setFormData(prev => ({
        ...prev,
        department: ''
      }));
    }

    // Clear sector/department for Admin and Planning Official roles
    if (field === 'role' && isNonDepartmentRole(value)) {
      setFormData(prev => ({
        ...prev,
        sector: '',
        department: ''
      }));
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sector;
        delete newErrors.department;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Only validate sector/department for Department users
    if (formData.role === USER_ROLES.DEPARTMENT) {
      if (!formData.sector) {
        newErrors.sector = 'Sector is required for department users';
      }
      
      if (!formData.department) {
        newErrors.department = 'Department is required for department users';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const userData = {
        fullName: formData.fullName.trim(),
        email: formData.email.toLowerCase().trim(),
        role: formData.role,
        status: formData.status
      };

      // Only add sector/department for Department users
      if (formData.role === USER_ROLES.DEPARTMENT) {
        userData.sector = formData.sector;
        userData.department = formData.department;
      } else {
        userData.sector = null;
        userData.department = null;
      }
      
      await onUserAdded(userData);
      
      setFormData({
        fullName: '',
        email: '',
        sector: '',
        department: '',
        role: USER_ROLES.DEPARTMENT,
        status: USER_STATUS.ACTIVE
      });
      setErrors({});
      
      onClose();
    } catch (error) {
      console.error('Error adding user:', error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        fullName: '',
        email: '',
        sector: '',
        department: '',
        role: USER_ROLES.DEPARTMENT,
        status: USER_STATUS.ACTIVE
      });
      setErrors({});
      onClose();
    }
  };

  const getDepartmentOptions = () => {
    if (isNonDepartmentRole(formData.role)) {
      return [{ value: '', label: 'Not applicable for this role' }];
    }

    if (!formData.sector) {
      return [{ value: '', label: 'Select a sector first' }];
    }
    
    if (loadingDepartments) {
      return [{ value: '', label: 'Loading departments...' }];
    }
    
    if (filteredDepartments.length === 0) {
      return [{ value: '', label: 'No departments available' }];
    }
    
    return filteredDepartments
      .sort((a, b) => (a.priority || 999) - (b.priority || 999))
      .map(dept => ({
        value: dept.departmentCode,
        label: dept.departmentName
      }));
  };

  const isNonDepartmentUser = isNonDepartmentRole(formData.role);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New User"
      size="lg"
      maxHeight="large"
      closeOnOverlay={!loading}
    >
      <form onSubmit={handleSubmit} className={containerPadding}>
        {errors.general && (
          <div className="mb-4 bg-error-subtle border border-border-error rounded-xl p-3">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-error">{errors.general}</span>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <Input
            label="Full Name"
            type="text"
            value={formData.fullName}
            onChange={(e) => handleInputChange('fullName', e.target.value)}
            placeholder="Enter full name"
            error={errors.fullName}
            required
            disabled={loading}
            className="text-sm"
          />

          <Input
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="Enter email address"
            error={errors.email}
            required
            disabled={loading}
            className="text-sm"
          />

          <div className={`grid ${gridCols} ${gridGap}`}>
            <Select
              label="Role"
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              options={[
                { value: USER_ROLES.DEPARTMENT, label: ROLE_LABELS[USER_ROLES.DEPARTMENT] },
                { value: USER_ROLES.PLANNING_OFFICIAL, label: ROLE_LABELS[USER_ROLES.PLANNING_OFFICIAL] },
                { value: USER_ROLES.DONER, label: ROLE_LABELS[USER_ROLES.DONER] },
                { value: USER_ROLES.ADMIN, label: ROLE_LABELS[USER_ROLES.ADMIN] }
              ]}
              required
              disabled={loading}
              className="text-sm"
            />

            <Select
              label="Account Status"
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              options={USER_STATUS_OPTIONS.map(status => ({
                value: status.value,
                label: status.label
              }))}
              required
              disabled={loading}
              className="text-sm"
            />
          </div>

          {isNonDepartmentUser && (
            <div className="bg-info-subtle border border-border-primary rounded-xl p-3">
              <div className="flex items-start">
                <svg className="w-4 h-4 text-info mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-info">
                  <p className="font-medium mb-1">
                    {ROLE_LABELS[formData.role]}
                  </p>
                  <p className="text-xs">Sector and department are not required for {ROLE_LABELS[formData.role]} accounts.</p>
                </div>
              </div>
            </div>
          )}

          {formData.role === USER_ROLES.DEPARTMENT && (
            <div className={`grid ${gridCols} ${gridGap}`}>
              <Select
                label="Sector"
                value={formData.sector}
                onChange={(e) => handleInputChange('sector', e.target.value)}
                options={[
                  { value: '', label: 'Select a sector' },
                  ...SECTORS
                ]}
                error={errors.sector}
                required
                disabled={loading}
                className="text-sm"
              />

              <Select
                label="Department"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
                options={getDepartmentOptions()}
                error={errors.department}
                required
                disabled={!formData.sector || loading || loadingDepartments}
                className="text-sm"
              />
            </div>
          )}
        </div>

        <div className={`flex ${buttonSpacing} mt-6 pt-4 border-t border-border-primary`}>
          <Button
            type="button"
            onClick={handleClose}
            variant="outline"
            disabled={loading}
            className={getResponsiveValue({ mobile: 'w-full', desktop: 'flex-1' })}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            loading={loading}
            disabled={loading || loadingDepartments}
            className={getResponsiveValue({ mobile: 'w-full', desktop: 'flex-1' })}
          >
            {loading ? 'Adding User...' : 'Add User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddUserModal;